/**
 * ANKER-SLUGS TIL AFSNIT ([D-198] — det globale søgefelt).
 *
 * Søgningen skal kunne springe ned til det AFSNIT et træf hører til, og det
 * kræver et id på afsnittets overskrift. Reglen bor her, fordi den bruges to
 * steder der SKAL være enige:
 *
 *   1. `SectionTitle.vue` sætter id'et når siden tegnes.
 *   2. `scripts/build-search-index.ts` skriver det samme id ind i søge-indekset
 *      når det genereres fra kildekoden.
 *
 * Er de to uenige, peger et søgeresultat på et anker der ikke findes, og
 * brugeren lander bare øverst på siden uden at få at vide hvorfor. Derfor én
 * funktion, ét sted.
 *
 * Æ/Ø/Å translittereres frem for at fjernes, så "Værdi pr. opkald" og
 * "Verdi pr. opkald" ikke kollapser til samme slug.
 */

const DANISH: Record<string, string> = {
  æ: 'ae',
  ø: 'oe',
  å: 'aa',
  ä: 'ae',
  ö: 'oe',
  ü: 'ue',
  é: 'e',
  è: 'e',
  ê: 'e',
  á: 'a',
  à: 'a',
  í: 'i',
  ó: 'o',
  ú: 'u',
};

/**
 * 'Rater i drift (~F)' → 'rater-i-drift-f'.
 *
 * Tomt/whitespace-input giver tom streng — kalderen skal da UNDLADE at sætte et
 * id frem for at lave et anker der hedder ingenting.
 */
export function slugifyAnchor(text: string): string {
  const lowered = text
    .normalize('NFC')
    .toLowerCase()
    // Tekst i parentes er ofte en kilde-angivelse eller et symbol (fx "(~F)");
    // den bevares som ord, men tegnene selv falder væk nedenfor.
    .replace(/[æøåäöüéèêáàíóú]/g, (ch) => DANISH[ch] ?? ch);
  const slug = lowered
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    // Meget lange overskrifter giver uhåndterlige ankre; 60 tegn er rigeligt
    // til at være unikt på en side.
    .slice(0, 60)
    .replace(/-+$/g, '');
  return slug;
}

/** Anker-prefix, så vores id'er ikke kan kollidere med tilfældige id'er i DOM'en. */
export const ANCHOR_PREFIX = 'sec-';

/** Fuldt element-id for et afsnit. Tom titel → null (intet anker). */
export function anchorId(text: string): string | null {
  const slug = slugifyAnchor(text);
  return slug.length > 0 ? `${ANCHOR_PREFIX}${slug}` : null;
}

// At scrolle til et anker (søgningen, delte links) bor i section-hash.ts ([D-554]),
// som også skriver afsnittet sidst i adressen.
